import React from "react"
import {
  Flex,
  Box,
  styled,
  forwardRef,
  createStyles,
  HTMLStyledProps,
  Text,
} from "@design-system/core"
import { createContext } from "@design-system/react-utils"
import {
  CheckCircleSolidIcon,
  WarningSolidIcon,
  InfoSolidIcon,
  QuestionSolidIcon,
  TagIcon,
  ErrorSolidIcon,
} from "@design-system/icons"
import { AlertProps, AlertContentProps, AlertTitleProps, AlertDescriptionProps } from "./types"

const STATUSES = {
  info: { icon: InfoSolidIcon },
  success: { icon: CheckCircleSolidIcon },
  error: { icon: ErrorSolidIcon },
  warning: { icon: WarningSolidIcon },
  educational: { icon: QuestionSolidIcon },
  promotional: { icon: TagIcon },
}

export type AlertStatus = keyof typeof STATUSES

interface AlertContext {
  status: AlertStatus
}

const [AlertProvider, useAlertContext] = createContext<AlertContext>({
  name: "AlertContext",
  errorMessage:
    "useAlertContext: `context` is undefined. Seems you forgot to wrap alert components in `<Alert />`",
})


export const Alert = forwardRef<AlertProps, "div">((props, ref) => {
  const { status = "info", quiet = false, ...rest } = props

  return (
    <AlertProvider value={{ status }}>
      <Flex
        role="alert"
        ref={ref}
        {...rest}
        style={[
          styles.container,
          !quiet && styles.status[status]?.container,
        ]}
      />
    </AlertProvider>
  )
})

export const AlertContent = forwardRef<AlertContentProps, "div">(
  (props, ref) => {
    return <Box ref={ref} {...props} />
  },
)

export const AlertTitle = forwardRef<AlertTitleProps, "div">((props, ref) => {
  return (
    <Text ref={ref} style={[styles.title]}>
      {props.children}
    </Text>
  )
})

export const AlertDescription = forwardRef<AlertDescriptionProps, "div">(
  (props, ref) => {
    return <Text ref={ref}>{props.children}</Text>
  },
)

export interface AlertIconProps extends HTMLStyledProps<"span"> {}

export const AlertIcon: React.FC<AlertIconProps> = (props) => {
  const { status } = useAlertContext()
  const BaseIcon  = STATUSES[status]?.icon

  return (
    <styled.span
      display="inherit"
      {...props}
      style={[styles.status[status]?.icon, styles.icon]}
    >
      <BaseIcon />
    </styled.span>
  )
}

const styles = createStyles((theme) => ({
  container: {
    alignItems: "center",
    borderRadius: theme.borderRadii.medium,
    fontSize: theme.textFontSizes.medium,
    position: 'relative',
    padding: theme.spaces.spacing16,
    paddingRight: theme.spaces.spacing48,
  },
  icon: {
    minWidth: theme.sizes[20],
    width: theme.sizes[20],
    marginRight: theme.spaces.spacing12,
  },
  title: {
    fontWeight: theme.fontWeights.bold,
    marginRight: theme.spaces.spacing12,
  },
  status: {
    error: {
      container: {
        backgroundColor: theme.colors.red050,
      },
      icon: {
        color: theme.colors.red600,
      },
    },
    success: {
      container: {
        backgroundColor: theme.colors.green050,
      },
      icon: {
        color: theme.colors.green400,
      },
    },
    warning: {
      container: {
        backgroundColor: theme.colors.yellow050,
      },
      icon: {
        color: theme.colors.yellow600,
      },
    },
    info: {
      container: {
        backgroundColor: theme.colors.blue050,
      },
      icon: {
        color: theme.colors.blue400,
      },
    },
    educational: {
      container: {
        backgroundColor: theme.colors.gray050,
      },
      icon: {
        color: theme.colors.black,
      },
    },
    promotional: {
      container: {
        backgroundColor: theme.colors.green050,
      },
      icon: {
        color: theme.colors.green400,
      },
    },
  },
}))
